import { ApplicationConfig, inject, provideBrowserGlobalErrorListeners, provideAppInitializer, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService } from 'primeng/api';
import { HexxaPreset } from './theme/hexxa-preset';
import localeEsCl from '@angular/common/locales/es-CL';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth-interceptor';
import { loadingInterceptor } from './core/interceptors/loading-interceptor';
import { ConfiguracionService } from './core/services/configuracion.service';
import { MarcaService } from './core/services/marca.service';
import { vigilarBotonesDePrimeNg } from './core/accesibilidad/etiquetas-primeng';
import { devolverFocoAlCerrarDialogos } from './core/accesibilidad/devolver-foco';
import { AuthService } from './core/services/auth';
import { VolverService } from './core/services/volver.service';

// Sin esto, el pipe `date` de Angular (no así el `toLocaleDateString()` nativo que usa
// el resto de la app) cae al locale por default 'en-US' — nombres de mes en inglés
// (visible solo en los 4 meses que difieren de su abreviatura en español: ene/abr/ago/dic,
// los otros 8 se ven "bien" por coincidencia). Registrarlo acá una sola vez corrige todos
// los usos de `| date` en la app en vez de parchear cada pantalla por separado.
registerLocaleData(localeEsCl, 'es-CL');

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-CL' },
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideAppInitializer(async () => {
      const auth   = inject(AuthService);
      const config = inject(ConfiguracionService);
      await Promise.all([auth.refreshMe(), config.cargar()]);
    }),
    // El historial de navegación del botón «volver» tiene que existir desde la primera navegación: se crea al arrancar, antes de ella.
    provideAppInitializer(() => { inject(VolverService); }),
    // La marca (nombre y logo) también fija el título de la pestaña: se crea al arrancar, antes de la primera pantalla.
    provideAppInitializer(() => { inject(MarcaService); }),
    // Los botones de ícono de los diálogos de PrimeNG salen sin nombre accesible: se les pone al aparecer.
    provideAppInitializer(() => { vigilarBotonesDePrimeNg(); }),
    provideAppInitializer(() => { devolverFocoAlCerrarDialogos(); }),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([authInterceptor, loadingInterceptor])
    ),
    // Un solo <p-confirmdialog> en MainLayout: las acciones destructivas de toda la app confirman
    // por acá (ver PageHeader), en vez de confirm() nativo.
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: HexxaPreset,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities'
          }
        }
      },
      ripple: true,
      // Escala de apilamiento única (styles.scss: --z-*, documentada en el CLAUDE.md del Front): los paneles de PrimeNG y los de la aplicación se ordenan con los mismos números.
      zIndex: { modal: 1100, overlay: 1000, menu: 1000, tooltip: 1100 },
      // V3: los paneles de Select, MultiSelect, DatePicker, AutoComplete y Popover (y los diálogos) se muestran en el <body> por defecto, no dentro de
      // quien los abre: dentro de un diálogo con scroll un panel se recortaba o tapaba el formulario. Un `appendTo` explícito en un componente sigue mandando.
      overlayAppendTo: 'body',
      // Sin esto PrimeNG muestra sus textos por defecto en inglés ("No results found" en un
      // select vacío). Los inputs de cada componente (emptyMessage, acceptLabel, etc.) siguen
      // teniendo prioridad sobre estos valores globales. No hay p-datepicker en la app (se usa
      // <input type="date"> nativo), por eso no se traducen nombres de días/meses.
      translation: {
        emptyMessage:          'No hay opciones disponibles',
        emptyFilterMessage:    'No se encontraron resultados',
        emptySearchMessage:    'No se encontraron resultados',
        emptySelectionMessage: 'Ningún elemento seleccionado',
        searchMessage:         '{0} resultados disponibles',
        selectionMessage:      '{0} elementos seleccionados',
        accept:  'Sí',
        reject:  'No',
        cancel:  'Cancelar',
        clear:   'Limpiar',
        apply:   'Aplicar',
        choose:  'Elegir',
        upload:  'Subir',
        noFilter: 'Sin filtro',
        pending: 'Pendiente',
        // Nombres accesibles de los controles de PrimeNG (paginador, diálogos, selección): sin esto salen en inglés («Next Page», «Rows per page»…).
        aria: {
          trueLabel: 'Verdadero', falseLabel: 'Falso', nullLabel: 'Sin elegir',
          star: '1 estrella', stars: '{star} estrellas',
          selectAll: 'Todos los elementos seleccionados', unselectAll: 'Ningún elemento seleccionado',
          close: 'Cerrar', previous: 'Anterior', next: 'Siguiente', navigation: 'Navegación', scrollTop: 'Volver arriba',
          moveTop: 'Mover al inicio', moveUp: 'Subir', moveDown: 'Bajar', moveBottom: 'Mover al final',
          moveToTarget: 'Mover al destino', moveToSource: 'Mover al origen', moveAllToTarget: 'Mover todos al destino', moveAllToSource: 'Mover todos al origen',
          pageLabel: 'Página {page}', firstPageLabel: 'Primera página', lastPageLabel: 'Última página', nextPageLabel: 'Página siguiente',
          prevPageLabel: 'Página anterior', previousPageLabel: 'Página anterior', rowsPerPageLabel: 'Filas por página',
          jumpToPageDropdownLabel: 'Ir a la página', jumpToPageInputLabel: 'Ir a la página',
          selectRow: 'Fila seleccionada', unselectRow: 'Fila no seleccionada', expandRow: 'Fila expandida', collapseRow: 'Fila contraída',
          showFilterMenu: 'Mostrar el menú de filtro', hideFilterMenu: 'Ocultar el menú de filtro', filterOperator: 'Operador del filtro', filterConstraint: 'Condición del filtro',
          editRow: 'Editar fila', saveEdit: 'Guardar cambios', cancelEdit: 'Cancelar edición',
          listView: 'Vista de lista', gridView: 'Vista de cuadrícula', slide: 'Diapositiva', slideNumber: '{slideNumber}', zoomImage: 'Ampliar imagen',
          zoomIn: 'Acercar', zoomOut: 'Alejar', rotateRight: 'Girar a la derecha', rotateLeft: 'Girar a la izquierda', listLabel: 'Lista de opciones',
          selectColor: 'Elegir color', removeLabel: 'Quitar', browseFiles: 'Buscar archivos', maximizeLabel: 'Maximizar', minimizeLabel: 'Minimizar',
        },
      },
    })
  ]
};
